// Composition model — the pure logic behind the "who drives this scope"
// orientation block (components/lenses/ScopeComposition.jsx) and the cluster
// Detail lens's composition panel.
//
// Three pure pieces, no data or DOM dependency:
//   • concentrationVerdict — turns a dim's top-N ranking + the scope total into
//     the one decide-sentence ("Concentrated — X drives ~83% …" vs "Diffuse —
//     spread across 46 endpoints …"). Dim-parameterized: the noun and the tail
//     phrasing change per dimension, so the same verdict reads right whether the
//     driver is a machine, a person, an agent, or a workload area.
//   • compositionDimsFor — which dims to profile a scope by, in priority order,
//     given what the scope is already pinned on. An endpoint scope shouldn't
//     "break down by endpoint"; it should lead with what that machine DOES.
//   • scopeNounFor — the scope's own noun ("this cluster", "this endpoint"),
//     used as the verdict's subject.

import { DIMENSIONS, isDimensionVisible, stepIds } from "./dimensionDefs.js";

// Concentration thresholds — top driver's share of the scope's total activity.
// ≥60% reads as one actor's workflow; ≥25% as a clear lead with a tail; below
// that, genuinely diffuse. Kept as the cluster lens's original cutoffs so the
// generalization is behavior-preserving for clusters.
const CONCENTRATED_AT = 60;
const LED_AT = 25;

// Per-dim phrasing. `one`/`many` are the singular/plural nouns; `verb` is how
// the top entry relates to the scope; `concentrated`/`diffuse` are the trailing
// interpretation clauses. A dim with no entry here falls back to phrasingFor's
// generic construction off its registry label.
const PHRASING = {
  endpoints: {
    one: "endpoint", many: "endpoints", verb: "drives",
    concentrated: "Likely one machine's workflow.",
    diffuse: "Reads as org-wide behavior.",
  },
  identities: {
    one: "person", many: "people", verb: "drives",
    concentrated: "Likely one person's work.",
    diffuse: "Reads as team-wide behavior.",
  },
  agents: {
    one: "agent", many: "agents", verb: "accounts for",
    concentrated: "Effectively single-agent.",
    diffuse: "A mix of agents.",
  },
  models: {
    one: "model", many: "models", verb: "accounts for",
    concentrated: "Effectively single-model.",
    diffuse: "A mix of models.",
  },
  sessions: {
    one: "session", many: "sessions", verb: "accounts for",
    concentrated: "Driven by one long session.",
    diffuse: "Many separate sessions.",
  },
  clusters: {
    one: "area", many: "areas", verb: "covers",
    concentrated: "A focused workload.",
    diffuse: "A broad mix of work.",
  },
  tools: {
    one: "tool", many: "tools", verb: "leads",
    concentrated: "Tool usage is concentrated.",
    diffuse: "A wide tool surface.",
  },
  mcp_servers: {
    one: "server", many: "servers", verb: "leads",
    concentrated: "One server dominates.",
    diffuse: "Several servers in play.",
  },
  skills: {
    one: "skill", many: "skills", verb: "leads",
    concentrated: "One skill dominates.",
    diffuse: "A broad skill surface.",
  },
};

function phrasingFor(dimKey) {
  if (PHRASING[dimKey]) return PHRASING[dimKey];
  // Generic fallback off the registry label: "Endpoints" -> "endpoint(s)".
  const label = (DIMENSIONS[dimKey]?.label || dimKey || "item").toLowerCase();
  const many = label;
  const one = many.endsWith("s") ? many.slice(0, -1) : many;
  return { one, many, verb: "accounts for", concentrated: "Concentrated.", diffuse: "Broadly spread." };
}

function plural(n, one, many) {
  return n === 1 ? one : many;
}

/**
 * The one-line concentration verdict for a dim's ranking within a scope.
 *
 * @param {object}   args
 * @param {Array}    args.entries     top-N entries [{ label, activity }, …]
 * @param {number}   args.total       distinct count of members in scope
 * @param {number}   args.scopeTotal  the scope's total activity (denominator)
 * @param {string}   args.dimKey      plural dim key (drives phrasing)
 * @param {string}   [args.subject]   the scope's own noun ("this cluster"); the
 *                                    verdict subject. Defaults to "this scope".
 * @returns {string|null} null when there's nothing to say (no entries / zero total)
 */
export function concentrationVerdict({ entries, total, scopeTotal, dimKey, subject = "this scope" }) {
  const top = entries && entries[0];
  if (!top || !(scopeTotal > 0)) return null;
  const ph = phrasingFor(dimKey);
  const share = Math.min(100, Math.round((top.activity / scopeTotal) * 100));
  const distinct = total ?? entries.length;
  if (share >= CONCENTRATED_AT) {
    return `Concentrated. ${top.label} ${ph.verb} ~${share}% of ${subject}. ${ph.concentrated}`;
  }
  if (share >= LED_AT) {
    const others = Math.max(0, distinct - 1);
    return `Led by ${top.label} (~${share}%), with ${others} other ${plural(others, ph.one, ph.many)} active.`;
  }
  return `Diffuse. Spread across ${distinct} ${plural(distinct, ph.one, ph.many)} with no single driver. ${ph.diffuse}`;
}

// The scope's own noun, used as the verdict subject. Keyed off the terminal
// step's dim; falls back to the neutral "this scope".
const SCOPE_NOUN = {
  clusters: "this topic",
  endpoints: "this endpoint",
  identities: "this person's work",
  agents: "this agent's traffic",
  models: "this model's traffic",
  sessions: "this session",
  tools: "this tool's use",
  mcp_servers: "this server's use",
  skills: "this skill's use",
};

export function scopeNounFor(scopePath) {
  const path = scopePath || [];
  if (path.length === 0) return "your organization";
  const terminal = path[path.length - 1];
  return SCOPE_NOUN[terminal.dim] || "this scope";
}

// The composition block's heading, phrased for the scope type: a workload area
// asks "who drives" it; a machine or person asks "what it does". Keyed off the
// terminal dim; falls back to the neutral "Composition".
const SCOPE_HEADING = {
  clusters: "Who drives this topic",
  endpoints: "What this endpoint does",
  identities: "What this person does",
  agents: "Who uses this agent",
  models: "Who uses this model",
  sessions: "What's in this session",
  tools: "Who uses this tool",
  mcp_servers: "Who uses this server",
  skills: "Who uses this skill",
};

export function compositionHeadingFor(scopePath) {
  const path = scopePath || [];
  if (path.length === 0) return "What your organization runs on";
  const terminal = path[path.length - 1];
  return SCOPE_HEADING[terminal.dim] || "Composition";
}

// Priority-ordered composition dims per terminal scope dim. The first is the
// PRIMARY (drives the verdict). Chosen so the block leads with the most
// decision-relevant "who/what" for that scope: inside an endpoint, what the
// machine does; inside a workload area, who drives it. Each list runs LONGER
// than the column count so the block still fills when a preferred dim is
// excluded (already pinned in the scope path, or hidden by a feature gate) —
// the filter in compositionDimsFor walks down the list until it has `count`
// visible dims. The generic default (tenant scope + any dim with no override)
// leads with the cluster lens's original endpoints/identities/agents.
const DIMS_BY_TERMINAL = {
  __default__: ["endpoints", "identities", "agents", "clusters", "models", "sessions"],
  endpoints:   ["clusters", "agents", "identities", "models", "sessions", "tools"],
  identities:  ["endpoints", "clusters", "agents", "models", "sessions", "tools"],
  agents:      ["endpoints", "identities", "clusters", "models", "sessions", "tools"],
  models:      ["endpoints", "identities", "clusters", "agents", "sessions", "tools"],
  sessions:    ["clusters", "tools", "agents", "endpoints", "models", "identities"],
  clusters:    ["endpoints", "identities", "agents", "models", "sessions", "tools"],
  tools:       ["endpoints", "agents", "clusters", "identities", "models", "sessions"],
  mcp_servers: ["tools", "endpoints", "agents", "clusters", "identities", "models"],
  skills:      ["endpoints", "agents", "clusters", "identities", "models", "sessions"],
};

// Dims that can't serve as composition columns: client-only synthetic dims
// (signals) and dims the scope is already pinned on (can't re-break-down by a
// dim already committed in the path). Also filtered to visible/analytics dims.
const NEVER_COMPOSE = new Set(["signals"]);

/**
 * Ordered list of up-to-`count` dim keys to profile a scope by. Excludes any dim
 * already in the scope path, the synthetic non-composable dims, and any dim
 * hidden by the current feature gates. At the tenant scope (empty path) it
 * returns the default triptych — the org's overall makeup.
 *
 * @param {Array}  scopePath  committed scope steps
 * @param {number} [count=3]
 * @returns {string[]}
 */
export function compositionDimsFor(scopePath, count = 3) {
  const path = scopePath || [];
  const terminal = path.length ? path[path.length - 1] : null;
  const pinned = new Set(path.map((s) => s.dim));
  const ordered = (terminal && DIMS_BY_TERMINAL[terminal.dim]) || DIMS_BY_TERMINAL.__default__;
  const out = [];
  for (const dim of ordered) {
    if (out.length >= count) break;
    if (pinned.has(dim)) continue;
    if (NEVER_COMPOSE.has(dim)) continue;
    if (!isDimensionVisible(dim)) continue;
    out.push(dim);
  }
  return out;
}

// The full universe of composable dims — the pool `candidateDimsFor` draws from
// once the per-terminal priority list is exhausted, so the distinctiveness
// ranker can consider everything, not just the default triptych.
const ALL_COMPOSE_DIMS = ["endpoints", "identities", "agents", "models", "clusters", "sessions", "tools", "mcp_servers", "skills"];

/**
 * Every dim worth SCORING for distinctiveness at a scope: not already pinned,
 * not a synthetic non-composable dim, visible under the gates — capped at
 * `count`. The per-terminal priority list leads (stable tie-break), then any
 * other visible dims. Unlike compositionDimsFor this returns the full pool; the
 * real ordering is by distinctiveness downstream.
 */
export function candidateDimsFor(scopePath, count = 8) {
  const path = scopePath || [];
  const terminal = path.length ? path[path.length - 1] : null;
  const pinned = new Set(path.map((s) => s.dim));
  const ordered = [...((terminal && DIMS_BY_TERMINAL[terminal.dim]) || DIMS_BY_TERMINAL.__default__), ...ALL_COMPOSE_DIMS];
  const out = [];
  const seen = new Set();
  for (const dim of ordered) {
    if (out.length >= count) break;
    if (seen.has(dim) || pinned.has(dim) || NEVER_COMPOSE.has(dim)) continue;
    if (!isDimensionVisible(dim)) continue;
    seen.add(dim);
    out.push(dim);
  }
  return out;
}

// ── Baseline lift ───────────────────────────────────────────────
//
// A composition row's raw share answers "how big is this member here". Lift
// answers the decision question "is this member UNUSUAL here" — its share of
// this scope over its share of the baseline (the tenant). Lift 2× means the
// member takes twice the share here it takes org-wide: it OVER-indexes on this
// scope. That's what turns the triptych from a description into a signal —
// "which endpoints are unusually Claude-heavy", "which models over-index on this
// cluster" — and it's the thing a consumer acts on.

// Only surface a deviation this far from parity (1×). Symmetric-ish around 1 so
// an over- and its reciprocal under- trip at the same magnitude.
export const LIFT_OVER = 1.35;
export const LIFT_UNDER = 0.74;
// Floors so a member with a negligible baseline presence can't manufacture a
// giant, meaningless lift, and a one-off here isn't called distinctive.
const MIN_BASE_SHARE = 0.005; // 0.5% of the org — below this the ratio is noise
const MIN_SCOPE_ACTIVITY = 3;

/**
 * scopeShare / baselineShare for one member, or null when either side is too
 * thin to compare. Both totals are the prompt totals of their scope.
 */
export function computeLift({ scopeActivity, scopeTotal, baseActivity, baseTotal }) {
  if (!(scopeTotal > 0) || !(baseTotal > 0)) return null;
  if (!(scopeActivity >= MIN_SCOPE_ACTIVITY)) return null;
  const baseShare = (Number(baseActivity) || 0) / baseTotal;
  if (!(baseShare >= MIN_BASE_SHARE)) return null;
  const scopeShare = scopeActivity / scopeTotal;
  return scopeShare / baseShare;
}

/**
 * The member that over-indexes hardest vs the org — the "most discriminating"
 * value to auto-drill into at a level. `orgActivityById` may be a Map or a
 * plain object (id → org activity). Returns { ...entry, lift } or null when no
 * member clears the lift floors (then the caller falls back to the top by
 * activity).
 */
// Compact multiplier text: 1.6 → "1.6", 2.4 → "2.4", 12 → "10+", 0.6 → "0.6".
export function formatLift(lift) {
  if (lift == null || !Number.isFinite(lift)) return "";
  if (lift >= 10) return "10+";
  return lift.toFixed(1);
}

/**
 * The presentation chip for a lift, or null when it's within the parity band
 * (no chip — keeps the surface a signal, not decoration).
 * @returns {{ dir: "over"|"under", text: string } | null}
 */
export function liftChip(lift) {
  if (lift == null || !Number.isFinite(lift)) return null;
  if (lift >= LIFT_OVER) return { dir: "over", text: `${formatLift(lift)}×` };
  if (lift <= LIFT_UNDER) return { dir: "under", text: `${formatLift(lift)}×` };
  return null;
}

/**
 * The single most over-indexed member across a set of composition columns — the
 * "what stands out" headline. Each column: { dimLabel, rows: [{ label, lift }] }.
 * Returns { dimLabel, label, lift } for the strongest over-index at/above
 * LIFT_OVER, or null when nothing is distinctive.
 */
export function topStandout(columns) {
  let best = null;
  for (const col of columns || []) {
    for (const row of col.rows || []) {
      if (row.lift == null || row.lift < LIFT_OVER) continue;
      if (!best || row.lift > best.lift) best = { dimLabel: col.dimLabel, label: row.label, lift: row.lift };
    }
  }
  return best;
}

/**
 * Correlation-pin state transition for ScopeComposition. Clicking a member
 * toggles it OFF if it's already the pinned one, else replaces the pin with it
 * — the only escape from focus besides the clear button. Pure so the toggle /
 * replace paths are testable without the component.
 * @param {{dim:string, id:string}|null} cur  current pin
 * @returns {{dim:string, id:string, label:string}|null} next pin (null = unpinned)
 */
export function nextPin(cur, dimKey, id, label) {
  if (cur && cur.dim === dimKey && cur.id === id) return null;
  return { dim: dimKey, id, label: label || id };
}

/**
 * The share/lift denominator for a composition column. A re-scoped column uses
 * the pin-intersection total (pinTotal) — but ONLY once it has loaded; while
 * pinTotal is still 0, hold scopeTotal and report liftReady=false so no
 * wrong-baseline lift badge flashes during the loading window.
 * @param {{rescoped:boolean, pinTotal:number, scopeTotal:number}} args
 * @returns {{effectiveTotal:number, liftReady:boolean}}
 */
export function resolveScopeDenominator({ rescoped, pinTotal, scopeTotal }) {
  return {
    effectiveTotal: rescoped && pinTotal > 0 ? pinTotal : scopeTotal,
    liftReady: !rescoped || pinTotal > 0,
  };
}

// Re-export for callers that build a step from a drilled entry id.
export { stepIds };
